import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';

import EuroIcon from '@mui/icons-material/Euro';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { SpeedDial } from '../../../components/SpeedDial';
import { RightDrawer } from '../../../components/RightDrawer';
import { useActionBlocker } from '../../../hooks/useActionBlocker.hook';
import { ResolvableMissionTypeList } from '../../../components/ResolvableMissionTypeList';

import { FormVariant } from '../../enums/variant.enum';
import { ResolvableMissionTypeForm } from '../../ResolvableMissionType';
import type { ResolvableMissionTypeMutationResult } from '../../ResolvableMissionType/hooks/useSubmit.hook';
import type {
  OptionalResolvableMissionTypeFormContext,
  RequiredResolvableMissionTypeFormContext,
} from '../../ResolvableMissionType/fragments/resolvable-mission-type.fragment';

import { useMissionTypeSelection } from '../hooks/useMissionTypeSelection.hook';

interface Props {
  readonly id: string;
}

// Fills the scrolling wizard step so the data grid scrolls its rows instead of
// growing with them; below 300px the step itself scrolls.
const EMBEDDED_LIST_SX = {
  boxSizing: 'border-box',
  height: '100%',
  minHeight: 300,
  pt: 3,
  pb: 1,
};

export const ResolvableMissionTypesSelectionFormStep =
  // eslint-disable-next-line no-empty-pattern
  ({}: Readonly<Props>) => {
    const { values } = useFormikContext<
      | OptionalResolvableMissionTypeFormContext
      | RequiredResolvableMissionTypeFormContext
    >();

    const selectType = useMissionTypeSelection();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const openDrawer = useCallback(() => {
      setIsDrawerOpen(true);
    }, []);
    const closeDrawer = useCallback(() => {
      setIsDrawerOpen(false);
    }, []);

    const handleSuccess = useCallback(
      (result: ResolvableMissionTypeMutationResult) => {
        setIsDrawerOpen(false);

        const { id } = ensureDefined({
          id:
            'createResolvableMissionType' in result
              ? result.createResolvableMissionType._id
              : 'findOneAndUpdateResolvableMissionType' in result
                ? result.findOneAndUpdateResolvableMissionType._id
                : undefined,
        });

        void selectType(id);
      },
      [selectType],
    );

    const [blockableCloseDrawer, setMustBlockAction] =
      useActionBlocker(closeDrawer);

    const handleRowSelectionModelChange = useCallback(
      (selection: Readonly<GridRowSelectionModel>) => {
        const selectedId = [...selection.ids].pop() as Id | undefined;
        void selectType(selectedId);
      },
      [selectType],
    );

    return (
      <>
        <ResolvableMissionTypeList
          checkboxSelection
          onRowSelectionModelChange={handleRowSelectionModelChange}
          rowSelectionModel={{
            type: 'include',
            ids: new Set(values.typeId ? [values.typeId] : EMPTY_ARRAY),
          }}
          sx={EMBEDDED_LIST_SX}
        />
        <SpeedDial
          actions={[
            {
              id: 'createResolvableMissionType',
              icon: <EuroIcon />,
              name: 'Créer Type de Mission',
              onClick: openDrawer,
            },
          ]}
          title="Actions possibles sur les Types de Mission"
        />
        <RightDrawer isOpen={isDrawerOpen} onDrawerClose={blockableCloseDrawer}>
          <ResolvableMissionTypeForm
            formVariant={FormVariant.Creation}
            onCancel={blockableCloseDrawer}
            onSuccess={handleSuccess}
            setMustBlockAction={setMustBlockAction}
            showCancelButton
            title="Formulaire de création d'un Type de Mission"
          />
        </RightDrawer>
      </>
    );
  };
